import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    MapPin, RefreshCw, Play, ChevronDown, ChevronRight, ExternalLink,
    CheckCircle, AlertCircle, XCircle, HelpCircle, Clock, AlertTriangle,
} from 'lucide-react';
import { GlassCard, GlassButton, GlassInput } from '../ui';
import {
    getBusinessNap, saveBusinessNap, runCitationAudit, getCitationAudit, getCitationHistory,
    BusinessNap, BusinessNapInput, CitationAuditRecord, DirectoryResult,
} from '../../services/api';
import './CitationAuditPanel.css';

// ── helpers ──────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 2500;
const POLL_CEILING_MS  = 180_000;  // 3 min max

/** Format a timestamp for display */
const fmtDate = (iso: string | null): string => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

/** Human-readable reason for a check_failed row. */
const failedReason = (reason: string | null): string => {
    switch (reason) {
        case 'robots_disallow': return 'Disallowed by robots.txt';
        case 'blocked_403':     return 'Directory blocks automated checks';
        case 'timeout':         return 'Request timed out';
        case 'parse_error':     return 'Could not parse directory page';
        case 'ssrf_blocked':    return 'SSRF guard blocked this URL';
        default:                return reason || 'Check failed';
    }
};

/** Status badge for a directory row. */
const StatusBadge: React.FC<{ status: DirectoryResult['status'] }> = ({ status }) => {
    switch (status) {
        case 'listed_consistent':
            return <span className="cap-badge cap-badge--consistent"><CheckCircle size={12} /> Consistent</span>;
        case 'listed_inconsistent':
            return <span className="cap-badge cap-badge--inconsistent"><AlertTriangle size={12} /> Inconsistent</span>;
        case 'not_found':
            return <span className="cap-badge cap-badge--notfound"><XCircle size={12} /> Not found</span>;
        case 'check_failed':
            return <span className="cap-badge cap-badge--failed"><HelpCircle size={12} /> Check failed</span>;
        default:
            return <span className="cap-badge cap-badge--failed">{status}</span>;
    }
};

// ── NAP editor card ───────────────────────────────────────────────────────────

interface NapEditorProps {
    projectId: string;
    onSaved: (nap: BusinessNap) => void;
}

const NapEditor: React.FC<NapEditorProps> = ({ projectId, onSaved }) => {
    const [form, setForm] = useState<BusinessNapInput>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);
    const [collapsed, setCollapsed] = useState(false);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        getBusinessNap(projectId)
            .then(res => {
                if (!mounted) return;
                if (res.nap) {
                    setForm({
                        business_name: res.nap.business_name || '',
                        address_line:  res.nap.address_line  || '',
                        locality:      res.nap.locality      || '',
                        region:        res.nap.region        || '',
                        postal_code:   res.nap.postal_code   || '',
                        country:       res.nap.country       || '',
                        phone:         res.nap.phone         || '',
                        website_url:   res.nap.website_url   || '',
                    });
                    setCollapsed(true);  // collapse if already saved
                }
            })
            .catch(() => setError('Could not load NAP record.'))
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [projectId]);

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSaved(false);
        try {
            const res = await saveBusinessNap(projectId, form);
            setSaved(true);
            setCollapsed(true);
            onSaved(res.nap);
            setTimeout(() => setSaved(false), 3000);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Failed to save NAP.');
        } finally {
            setSaving(false);
        }
    };

    const set = (field: keyof BusinessNapInput) => (value: string) =>
        setForm(prev => ({ ...prev, [field]: value }));

    if (loading) {
        return (
            <div className="cap-nap-loading">
                <RefreshCw size={14} className="cap-spinning" />
                <span>Loading business NAP…</span>
            </div>
        );
    }

    const hasName = !!(form.business_name || '').trim();
    const summaryLine = hasName
        ? `${form.business_name}${form.locality ? ` · ${form.locality}` : ''}${form.phone ? ` · ${form.phone}` : ''}`
        : 'No NAP saved yet — enter your business details below';

    return (
        <div className="cap-nap-card">
            <div className="cap-nap-header" onClick={() => setCollapsed(c => !c)} role="button" tabIndex={0}
                 onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setCollapsed(c => !c); }}>
                <div className="cap-nap-header-left">
                    <MapPin size={14} />
                    <span className="cap-nap-title">Business NAP (Canonical)</span>
                    {collapsed && <span className="cap-nap-summary">{summaryLine}</span>}
                </div>
                {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            </div>

            {!collapsed && (
                <div className="cap-nap-body">
                    <p className="cap-nap-hint">
                        Enter your exact business Name, Address, and Phone as they should appear across all directories.
                        This is the canonical record used to detect inconsistencies.
                    </p>
                    <div className="cap-nap-grid">
                        <div className="cap-nap-field cap-nap-field--full">
                            <label>Business Name</label>
                            <GlassInput
                                value={form.business_name || ''}
                                onChange={set('business_name')}
                                placeholder="e.g. Acme Plumbing Pty Ltd"
                            />
                        </div>
                        <div className="cap-nap-field cap-nap-field--full">
                            <label>Street Address</label>
                            <GlassInput
                                value={form.address_line || ''}
                                onChange={set('address_line')}
                                placeholder="e.g. 12 Smith Street"
                            />
                        </div>
                        <div className="cap-nap-field">
                            <label>Suburb / City</label>
                            <GlassInput
                                value={form.locality || ''}
                                onChange={set('locality')}
                                placeholder="e.g. Brisbane"
                            />
                        </div>
                        <div className="cap-nap-field">
                            <label>State / Region</label>
                            <GlassInput
                                value={form.region || ''}
                                onChange={set('region')}
                                placeholder="e.g. QLD"
                            />
                        </div>
                        <div className="cap-nap-field">
                            <label>Postcode</label>
                            <GlassInput
                                value={form.postal_code || ''}
                                onChange={set('postal_code')}
                                placeholder="e.g. 4000"
                            />
                        </div>
                        <div className="cap-nap-field">
                            <label>Country (ISO-2)</label>
                            <GlassInput
                                value={form.country || ''}
                                onChange={set('country')}
                                placeholder="e.g. AU"
                            />
                        </div>
                        <div className="cap-nap-field">
                            <label>Phone</label>
                            <GlassInput
                                value={form.phone || ''}
                                onChange={set('phone')}
                                placeholder="e.g. +61 7 3000 0000"
                            />
                        </div>
                        <div className="cap-nap-field">
                            <label>Website URL</label>
                            <GlassInput
                                value={form.website_url || ''}
                                onChange={set('website_url')}
                                placeholder="e.g. https://acmeplumbing.com.au"
                            />
                        </div>
                    </div>
                    {error && (
                        <div className="cap-error">
                            <AlertCircle size={14} />
                            <span>{error}</span>
                        </div>
                    )}
                    <div className="cap-nap-actions">
                        <GlassButton
                            variant="primary"
                            size="sm"
                            pill
                            loading={saving}
                            onClick={handleSave}
                        >
                            {saving ? 'Saving…' : saved ? 'Saved!' : 'Save NAP'}
                        </GlassButton>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Directory result row ──────────────────────────────────────────────────────

const DirectoryRow: React.FC<{ result: DirectoryResult }> = ({ result }) => {
    const [expanded, setExpanded] = useState(false);
    const hasDiffs = result.diffs && result.diffs.length > 0;

    return (
        <>
            <tr
                className={`cap-dir-row${hasDiffs ? ' cap-dir-row--expandable' : ''}`}
                onClick={() => hasDiffs && setExpanded(e => !e)}
            >
                <td className="cap-dir-name">
                    {hasDiffs && (
                        <span className="cap-expand-icon">
                            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        </span>
                    )}
                    {result.directory_name}
                </td>
                <td>
                    <StatusBadge status={result.status} />
                </td>
                <td className="cap-dir-detail">
                    {result.status === 'listed_inconsistent' && hasDiffs && (
                        <span className="cap-diff-hint">
                            {result.diffs.length} field{result.diffs.length > 1 ? 's differ' : ' differs'} — click to expand
                        </span>
                    )}
                    {result.status === 'check_failed' && (
                        <span className="cap-fail-reason" title={result.reason || ''}>
                            {failedReason(result.reason)}
                        </span>
                    )}
                    {result.status === 'not_found' && result.search_url && (
                        <a
                            className="cap-search-link"
                            href={result.search_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                        >
                            Search <ExternalLink size={11} />
                        </a>
                    )}
                    {result.listing_url && (
                        <a
                            className="cap-listing-link"
                            href={result.listing_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                        >
                            View listing <ExternalLink size={11} />
                        </a>
                    )}
                </td>
            </tr>
            {expanded && hasDiffs && (
                <tr className="cap-diff-row">
                    <td colSpan={3}>
                        <div className="cap-diff-table-wrap">
                            <table className="cap-diff-table">
                                <thead>
                                    <tr>
                                        <th>Field</th>
                                        <th>Your canonical</th>
                                        <th>Found in directory</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.diffs.map((d, i) => (
                                        <tr key={i}>
                                            <td className="cap-diff-field">{d.field}</td>
                                            <td className="cap-diff-canonical">{d.canonical}</td>
                                            <td className="cap-diff-found">{d.found}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

// ── Main panel ────────────────────────────────────────────────────────────────

interface CitationAuditPanelProps {
    projectId: string;
}

const CitationAuditPanel: React.FC<CitationAuditPanelProps> = ({ projectId }) => {
    const [napSaved, setNapSaved] = useState(false);
    const [napCountry, setNapCountry] = useState<string>('');
    const [auditRecord, setAuditRecord] = useState<CitationAuditRecord | null>(null);
    const [history, setHistory] = useState<CitationAuditRecord[]>([]);
    const [polling, setPolling] = useState(false);
    const [running, setRunning] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [histLoading, setHistLoading] = useState(false);
    const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pollStart = useRef<number>(0);

    // Load history on mount
    const loadHistory = useCallback(async () => {
        setHistLoading(true);
        try {
            const rows = await getCitationHistory(projectId);
            setHistory(rows);
            // If the most recent run is still running, resume polling it
            const latest = rows[0];
            if (latest && latest.status === 'running') {
                setAuditRecord(latest);
                startPolling(latest.citation_audit_id);
            } else if (latest && latest.status !== 'running') {
                setAuditRecord(latest);
            }
        } catch { /* silent */ } finally {
            setHistLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    useEffect(() => {
        loadHistory();
        return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    // Also check initial NAP saved state
    useEffect(() => {
        getBusinessNap(projectId).then(res => {
            if (res.nap) {
                setNapSaved(true);
                setNapCountry(res.nap.country || '');
            }
        }).catch(() => {});
    }, [projectId]);

    const startPolling = useCallback((auditId: string) => {
        setPolling(true);
        pollStart.current = Date.now();

        const tick = async () => {
            try {
                const rec = await getCitationAudit(auditId);
                setAuditRecord(rec);
                if (rec.status === 'running') {
                    const elapsed = Date.now() - pollStart.current;
                    if (elapsed < POLL_CEILING_MS) {
                        pollTimer.current = setTimeout(tick, POLL_INTERVAL_MS);
                        return;
                    }
                }
            } catch { /* ignore poll errors */ }
            setPolling(false);
            // refresh history
            getCitationHistory(projectId).then(rows => setHistory(rows)).catch(() => {});
        };
        pollTimer.current = setTimeout(tick, POLL_INTERVAL_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const handleNapSaved = (nap: BusinessNap) => {
        setNapSaved(true);
        setNapCountry(nap.country || '');
    };

    const handleRunAudit = async () => {
        setRunning(true);
        setError(null);
        const locale = napCountry.toUpperCase() === 'AU' ? 'en-AU' : 'en-US';
        try {
            const res = await runCitationAudit(projectId, locale);
            // Create a stub record while we wait
            setAuditRecord({
                citation_audit_id: res.citation_audit_id,
                project_id: projectId,
                nap_snapshot: null,
                locale,
                status: 'running',
                directories_total: res.directories_count,
                consistent_count: null,
                inconsistent_count: null,
                not_found_count: null,
                check_failed_count: null,
                results_json: null,
                error_message: null,
                created_at: new Date().toISOString(),
                completed_at: null,
            });
            startPolling(res.citation_audit_id);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Failed to start citation audit.');
        } finally {
            setRunning(false);
        }
    };

    // ── render ────────────────────────────────────────────────────────────────

    const results = auditRecord?.results_json ?? null;
    const isRunning = auditRecord?.status === 'running' || polling;

    const rollup = auditRecord && auditRecord.status === 'complete' ? [
        { label: 'Consistent',  count: auditRecord.consistent_count   ?? 0, cls: 'cap-rollup--ok' },
        { label: 'Inconsistent', count: auditRecord.inconsistent_count ?? 0, cls: 'cap-rollup--warn' },
        { label: 'Not found',   count: auditRecord.not_found_count     ?? 0, cls: 'cap-rollup--miss' },
        { label: 'Check failed', count: auditRecord.check_failed_count ?? 0, cls: 'cap-rollup--fail' },
    ] : null;

    return (
        <GlassCard className="cap-panel">
            <div className="cap-header">
                <div className="cap-title">
                    <MapPin size={16} />
                    <span>Citation Audit</span>
                </div>
                <div className="cap-header-right">
                    <GlassButton
                        variant="ghost"
                        size="sm"
                        onClick={loadHistory}
                        disabled={histLoading || isRunning}
                        title="Refresh history"
                        icon={<RefreshCw size={16} className={histLoading ? 'cap-spinning' : ''} />}
                    />
                </div>
            </div>
            <p className="cap-subtitle">
                Check whether your business Name, Address &amp; Phone are listed and consistent
                across the top local directories. Some directories block automated checks — those
                will show as "Check failed" rather than a fabricated status.
            </p>

            {/* NAP editor */}
            <NapEditor projectId={projectId} onSaved={handleNapSaved} />

            {/* Run button */}
            <div className="cap-run-row">
                <GlassButton
                    variant="primary"
                    size="sm"
                    pill
                    disabled={!napSaved || isRunning}
                    loading={running}
                    onClick={handleRunAudit}
                >
                    <Play size={14} />
                    {isRunning ? 'Running…' : 'Run Citation Audit'}
                </GlassButton>
                {!napSaved && (
                    <span className="cap-run-hint">Save your business NAP above to enable the audit.</span>
                )}
            </div>

            {error && (
                <div className="cap-error">
                    <AlertCircle size={14} />
                    <span>{error}</span>
                </div>
            )}

            {/* Running state */}
            {isRunning && (
                <div className="cap-loading">
                    <RefreshCw size={16} className="cap-spinning" />
                    <span>Checking directories…{auditRecord?.directories_total ? ` (${auditRecord.directories_total} directories)` : ''}</span>
                </div>
            )}

            {/* Results */}
            {!isRunning && auditRecord && auditRecord.status === 'complete' && results && results.length > 0 && (
                <div className="cap-results">
                    {/* Rollup summary */}
                    {rollup && (
                        <div className="cap-rollup">
                            {rollup.map(r => (
                                <div key={r.label} className={`cap-rollup-item ${r.cls}`}>
                                    <span className="cap-rollup-count">{r.count}</span>
                                    <span className="cap-rollup-label">{r.label}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <p className="cap-results-ts">
                        Completed {fmtDate(auditRecord.completed_at)}
                    </p>
                    <table className="seo-keyword-table cap-table">
                        <thead>
                            <tr>
                                <th>Directory</th>
                                <th>Status</th>
                                <th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.map(r => (
                                <DirectoryRow key={r.directory_key} result={r} />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!isRunning && auditRecord && auditRecord.status === 'failed' && (
                <div className="cap-error">
                    <XCircle size={14} />
                    <span>Audit failed: {auditRecord.error_message || 'Unknown error'}</span>
                </div>
            )}

            {!isRunning && (!auditRecord || auditRecord.status !== 'complete') && !error && (
                <div className="cap-empty">
                    <MapPin size={32} className="cap-empty-icon" />
                    <p>No citation audit run yet for this project.</p>
                    <p className="cap-empty-hint">
                        Save your business NAP above, then click "Run Citation Audit".
                    </p>
                </div>
            )}

            {/* History */}
            {history.length > 1 && (
                <div className="cap-history">
                    <h4 className="cap-history-title">
                        <Clock size={13} />
                        Past Runs
                    </h4>
                    <table className="seo-keyword-table cap-history-table">
                        <thead>
                            <tr>
                                <th>Started</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Consistent</th>
                                <th style={{ textAlign: 'right' }}>Inconsistent</th>
                                <th style={{ textAlign: 'right' }}>Not found</th>
                                <th style={{ textAlign: 'right' }}>Failed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.slice(1).map(r => (
                                <tr
                                    key={r.citation_audit_id}
                                    className="cap-history-row"
                                    onClick={() => getCitationAudit(r.citation_audit_id).then(full => setAuditRecord(full)).catch(() => setAuditRecord(r))}
                                    title="Click to view this run"
                                >
                                    <td>{fmtDate(r.created_at)}</td>
                                    <td>{r.status}</td>
                                    <td style={{ textAlign: 'right' }}>{r.consistent_count ?? '—'}</td>
                                    <td style={{ textAlign: 'right' }}>{r.inconsistent_count ?? '—'}</td>
                                    <td style={{ textAlign: 'right' }}>{r.not_found_count ?? '—'}</td>
                                    <td style={{ textAlign: 'right' }}>{r.check_failed_count ?? '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </GlassCard>
    );
};

export default CitationAuditPanel;
